import {
  CheckboxController,
  Collapse,
  Text,
} from "@pushpress/shared-components";
import { useRef } from "react";
import { Control, Controller, useWatch } from "react-hook-form";
import { PlanForm } from "../PlanDrawer.types";
import { useExperiment } from "../../../../hooks/useExperiment";
import { Input } from "@pushpress/pushpress-ui";

interface BillingAdvancedOptionsCollapseProps {
  control: Control<PlanForm>;
}

const BillingAdvancedOptionsCollapse = (props: BillingAdvancedOptionsCollapseProps) => {
  const { control } = props;
  const perPersonLimitRef = useRef<HTMLDivElement>(null);
  const quantityLimitRef = useRef<HTMLDivElement>(null);
  const [hasLimit] = useWatch({ control, name: ["hasLimit"] });
  const [perPersonLimit] = useWatch({ control, name: ["perPersonLimit"] });
  const { useExperimentValue } = useExperiment();
  const perPersonLimitFeatureFlag = useExperimentValue("plan_per_person_limit", false);
  const forcePurchaseStartDateFeatureFlag = useExperimentValue("plan-custom-start-date-landing-pages", false);

  return (
    <div className="tw-bg-white tw-rounded-lg tw-border tw-border-wolf-200 tw-p-6 tw-mt-4 tw-relative">
      <Text 
        component="h2"
        variant="heading-5"
        weight="600"
        color="wolf-800"
        className="tw-mb-2">
          Advanced Purchase Settings
      </Text>
      <div className="tw-my-6">
        <CheckboxController
          control={control}
          name="hasLimit"
          label="Limit quantity"
          className="mt-3"
        />
        <Text variant="body-md" color="wolf-600" className="mt-1">
          Enable this option if you want to sell this plan a specific number of times before it becomes unavailable.
        </Text>
        <Collapse in={hasLimit} ref={quantityLimitRef}>
          <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-3">
            <label htmlFor="quantityAvailable">
              Quantity *
            </label>
            <Controller
              control={control}
              name="quantityAvailable"
              render={({ field }) => (
              <Input
                placeholder="Quantity"
                {...field}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(parseInt(value));
                }}
                type="number"
                min={1}
                autoComplete="off"
                required
                name="quantityAvailable"
                />
                )}
            />
          </div>
        </Collapse>
        {perPersonLimitFeatureFlag && (
          <div>
            <CheckboxController
              control={control}
              name="perPersonLimit"
              label="Limit Per Person"
              className="mt-3"
            />
            <Text variant="body-md" color="wolf-600" className="mt-1">
              Enable this option if a client should only be able to purchase this plan a specific number of times.
            </Text>
            <Collapse in={perPersonLimit} ref={perPersonLimitRef}>
              <div className="tw-grid tw-items-center tw-gap-1.5 tw-mt-3">
                <label htmlFor="perPersonLimitNum">
                  Per person limit *
                </label>
                <Controller
                  control={control}
                  name="perPersonLimitNum"
                  render={({ field }) => (
                  <Input
                    value={field.value?.toString() || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(parseInt(value));
                    }}
                    placeholder="Per person limit"
                    type="number"
                    min={1}
                    autoComplete="off"
                    required
                    name="perPersonLimitNum"
                    />
                    )}
                  />
              </div>
            </Collapse>
          </div>
        )}
        {forcePurchaseStartDateFeatureFlag && (
          <div>
            <CheckboxController
              control={control}
              name="forcePurchaseStartDate"
              label="Force Purchase Start Date"
              className="mt-3"
            />
            <Text variant="body-md" color="wolf-600" className="mt-1">
              Enable this option if a client is forced to start their plan immediately upon purchase.
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingAdvancedOptionsCollapse; 